import { QueryClient } from '@tanstack/react-query'

/**
 * Custom error type for axios responses
 */
interface AxiosError {
  response?: {
    status?: number
  }
}

/**
 * Determines if an error is a client error (4xx) that shouldn't be retried
 */
const isNonRetriableClientError = (error: unknown): boolean => {
  const axiosError = error as AxiosError
  if (
    axiosError?.response?.status &&
    axiosError.response.status >= 400 &&
    axiosError.response.status < 500 &&
    axiosError.response.status !== 429 // Retry on rate limit errors
  ) {
    return true
  }
  return false
}

/**
 * Configured QueryClient instance with optimized retry logic and caching
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes before considering it stale
      staleTime: 5 * 60 * 1000,

      // Custom retry logic for queries
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (except 429 rate limit)
        if (isNonRetriableClientError(error)) {
          return false
        }
        // Retry up to 3 times for other errors
        return failureCount < 3
      },

      // Exponential backoff with max delay of 30 seconds
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },

    mutations: {
      // Custom retry logic for mutations
      retry: (failureCount, error) => {
        // Don't retry mutations on 4xx errors
        if (isNonRetriableClientError(error)) {
          return false
        }
        // Retry mutations up to 2 times (less aggressive than queries)
        return failureCount < 2
      },
    },
  },
})
